// MUNYA OS · T4 § 11.ter · NPS interno trimestral
// Banner discreto que aparece cuando el usuario no ha respondido el NPS del trimestre actual.
// Si responde, no vuelve a mostrarse hasta el siguiente trimestre.

import { useEffect, useState, type ReactElement } from 'react';
import { ChartLine, X } from '@phosphor-icons/react';
import { useDismissable } from '../lib/use-dismissable.js';

interface NpsExisting {
  id: string;
  quarter: string;
  nps_score: number;
}

interface NpsCheckResp {
  quarter: string;
  response: NpsExisting | null;
}

const DISMISS_KEY_PREFIX = 'munya_nps_dismissed_';

export function NPSBanner(): ReactElement | null {
  const [shouldShow, setShouldShow] = useState(false);
  const [quarter, setQuarter] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [mostTimeSaved, setMostTimeSaved] = useState('');
  const [mostTimeLost, setMostTimeLost] = useState('');
  const [dreamImprovement, setDreamImprovement] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/v1/nps/mine', { credentials: 'same-origin' });
        if (!res.ok) return; // 403 si no es interno, ignorar
        const data = (await res.json()) as NpsCheckResp;
        if (cancelled) return;
        setQuarter(data.quarter);
        const dismissed = localStorage.getItem(DISMISS_KEY_PREFIX + data.quarter);
        if (!data.response && !dismissed) setShouldShow(true);
      } catch {
        // silent
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function dismiss(): void {
    setShouldShow(false);
    localStorage.setItem(DISMISS_KEY_PREFIX + quarter, String(Date.now()));
  }

  async function submit(): Promise<void> {
    if (score === null) {
      setErr('Selecciona un puntaje 0-10');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const res = await fetch('/api/v1/nps', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          quarter,
          nps_score: score,
          most_time_saved: mostTimeSaved.trim() || undefined,
          most_time_lost: mostTimeLost.trim() || undefined,
          dream_improvement: dreamImprovement.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const b = (await res.json().catch(() => ({}))) as { title?: string };
        throw new Error(b.title ?? `HTTP ${res.status}`);
      }
      setShouldShow(false);
      setOpen(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  }

  // · #456 · el fondo solo cierra con el gesto completo, y avisa antes de tirar lo escrito
  const { backdropProps, discardConfirm } = useDismissable({
    onDismiss: () => {
      setOpen(false);
    },
    enabled: !submitting,
    dirty:
      score !== null ||
      [mostTimeSaved, mostTimeLost, dreamImprovement].some((v) => v.trim() !== ''),
    escape: true,
  });

  if (!shouldShow) return null;

  return (
    <>
      <div
        role="region"
        aria-label="Encuesta de satisfacción NPS"
        className="fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] lg:bottom-20 right-6 z-30 max-w-xs bg-[var(--color-munya-coffee)] text-[var(--color-munya-cream)] rounded-lg shadow-2xl p-4"
      >
        <div className="flex items-start gap-2 mb-2">
          <ChartLine size={18} weight="duotone" className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-sm leading-tight">¿Cómo va MUNYA OS?</p>
            <p className="text-xs opacity-70 mt-1">2 min en responder · trimestral · {quarter}</p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Descartar"
            className="opacity-60 hover:opacity-100 shrink-0"
          >
            <X size={14} />
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            setOpen(true);
          }}
          className="w-full mt-2 px-3 py-1.5 text-xs rounded-md bg-[var(--color-munya-olive)] text-white font-medium hover:bg-[var(--color-munya-olive-hover)] transition"
        >
          Responder
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          {...backdropProps}
        >
          <div
            className="bg-[var(--color-munya-cream)] rounded-lg shadow-2xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-base">NPS interno · {quarter}</h3>
                <p className="text-xs opacity-60 mt-0.5">
                  Tu respuesta es confidencial. Agregada solo se ve por Dirección + Jota.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                }}
                disabled={submitting}
                aria-label="Cerrar"
                className="opacity-50 hover:opacity-100"
              >
                <X size={18} />
              </button>
            </div>

            <p className="text-sm font-medium mb-2">
              1. ¿Qué tan probable es que recomiendes MUNYA OS a otro estudio? (0-10)
            </p>
            <div className="flex flex-wrap gap-1 mb-4">
              {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => {
                    setScore(n);
                  }}
                  className={`w-8 h-8 text-xs rounded ${
                    score === n
                      ? 'bg-[var(--color-munya-olive)] text-white font-semibold'
                      : 'bg-[var(--color-munya-surface)] border border-[var(--color-munya-sand)] hover:bg-[var(--color-munya-sand)]/30'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>

            <label className="block text-sm font-medium mb-1">2. ¿Qué te ahorra más tiempo?</label>
            <textarea
              value={mostTimeSaved}
              onChange={(e) => {
                setMostTimeSaved(e.target.value);
              }}
              rows={2}
              placeholder="opcional"
              className="w-full px-3 py-2 text-sm bg-[var(--color-munya-surface)] border border-[var(--color-munya-sand)] rounded-md mb-3 focus:outline-none focus:border-[var(--color-munya-olive)]"
            />

            <label className="block text-sm font-medium mb-1">3. ¿Qué te hace perder tiempo?</label>
            <textarea
              value={mostTimeLost}
              onChange={(e) => {
                setMostTimeLost(e.target.value);
              }}
              rows={2}
              placeholder="opcional"
              className="w-full px-3 py-2 text-sm bg-[var(--color-munya-surface)] border border-[var(--color-munya-sand)] rounded-md mb-3 focus:outline-none focus:border-[var(--color-munya-olive)]"
            />

            <label className="block text-sm font-medium mb-1">
              4. Si pudieras pedir una mejora ahora, ¿cuál?
            </label>
            <textarea
              value={dreamImprovement}
              onChange={(e) => {
                setDreamImprovement(e.target.value);
              }}
              rows={2}
              placeholder="opcional"
              className="w-full px-3 py-2 text-sm bg-[var(--color-munya-surface)] border border-[var(--color-munya-sand)] rounded-md mb-4 focus:outline-none focus:border-[var(--color-munya-olive)]"
            />

            {err && <p className="text-xs text-[var(--color-munya-destructive)] mb-3">{err}</p>}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                }}
                disabled={submitting}
                className="px-3 py-1.5 text-xs rounded border border-[var(--color-munya-sand)] hover:bg-[var(--color-munya-sand)]/30 disabled:opacity-50"
              >
                Más tarde
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={submitting || score === null}
                className="px-3 py-1.5 text-xs rounded bg-[var(--color-munya-olive)] text-white disabled:opacity-50"
              >
                {submitting ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
            {discardConfirm}
          </div>
        </div>
      )}
    </>
  );
}
